import {useAuth} from "../../../context";
import {useEffect, useState} from "react";
import {fetchWrapper} from "../../../_services/fetch";
import {useParams, useSearchParams} from "react-router-dom";
import * as yup from "yup";
import {endpoints, handleError, handleSuccess} from "../../../utils2";


const defaultValues = {
    id: null,
    name: "",
    line1: "",
    zip: "",
    country: "",
    city: "",
    region: "",
    prefix: "",
    phone: "",
    main: false,
    line2: "",
    note: "",
    created_at: null,
    updated_at: null,
}

export function useAddress(userId, aid = null) {
    const {uid} = useParams()

    const validationSchema = yup.object().shape({
        name: yup.string().required("Contact name cannot be empty"),
        prefix: yup.number().required("Missing prefix"),
        phone: yup.number().required("Missing phone"),
        line1: yup.string().required('Line1 is required'),
        country: yup.string().required('Country cannot be empty'),
        region: yup.string().required('Region is required'),
        city: yup.string().required('City cannot be empty'),
        zip: yup.string().required("Zip is required"),
    })

    const {authToken} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useAddressDetails(aid, uid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? resp.data : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(err)
        return Promise.reject(err)
    }


    function addItem(formValue) {
        const {id, ...valuesNoId} = formValue
        return fetchWrapper.post(endpoints.address.add(userId), valuesNoId, authToken.jwt)
            .then(handleSuccessInt)
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = aid) {
        return fetchWrapper.put(endpoints.address.edit(userId, targetId), formValue, authToken.jwt)
            .then(handleSuccessInt)
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = aid) {
        return fetchWrapper.delete(endpoints.address.delete(userId, targetId), authToken.jwt)
            .then(handleSuccessInt)
            .catch(handleError)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }


    return {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}


export function useAddressDetails(addressId, uid, defaultValue = defaultValues) {

    const {loggedIn, authToken} = useAuth()
    const [loadingValue, setLoadingValue] = useState(true)
    const [errorValue, setErrorValue] = useState(null)
    const [value, setValue] = useState(defaultValue)

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && addressId && addressId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpoints.address.get(uid, addressId), requestOptions)
                .then(handleSuccess)
                .then(setValue)
                .catch(handleError)
                .catch(setErrorValue)
                .finally(() => setLoadingValue(false))
        } else {
            setLoadingValue(false)
        }

        // return () => {
        //     controller.abort()
        //     setLoadingValue(false)
        // }
    }, [loggedIn, addressId])


    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useAddressCount(queryOption = null) {

    const {loggedIn, userId, authToken} = useAuth()
    const {uid} = useParams()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(true)
    const [errorCount, setErrorCount] = useState(null)
    const [count, setCount] = useState(0)

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query}

            fetchWrapper.generic(endpoints.address.count(uid || userId, search), requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(handleError)
                .catch(setErrorCount)
                .finally(() => setLoadingCount(false))
        } else {
            setLoadingCount(false)
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query])

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}

export function useAddressList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const {loggedIn, userId, authToken} = useAuth()
    const {uid} = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(true)
    const [errorList, setErrorList] = useState(null)
    const [list, setList] = useState([])
    const [reload, setReload] = useState(0)

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size"))
    const pageIndex = pageIndexOption || Number(searchParams.get("index"))
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }

            fetchWrapper.generic(endpoints.address.list(uid || userId, search), requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, ...data]) : data))
                .catch(handleError)
                .catch(setErrorList)
                .finally(() => setLoadingList(false))
        } else {
            setLoadingList(false)
        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [uid, loggedIn, pageIndex, pageSize, query, orderBy, reload]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useAddressOrderOptions(defaultOptions = []) {

    const {loggedIn, authToken} = useAuth()
    const {uid} = useParams()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${authToken.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpoints.address.order(), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(handleError)
                .catch(setErrorOrderOptions)
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}